import { Link } from "react-router-dom";
import { Facebook, Instagram, Mail, MapPin, Phone } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { siteConfig, fullAddress } from "../config/site";
import { track } from "../lib/tracking";

export const Footer = () => {
  const { t } = useLanguage();
  const year = new Date().getFullYear();
  const pages = [
    { to: "/", label: t.nav.home }, { to: "/zimmer", label: t.nav.rooms }, { to: "/ausstattung", label: t.nav.amenities }, { to: "/ueber-uns", label: t.nav.about },
    { to: "/lage", label: t.nav.location }, { to: "/galerie", label: t.nav.gallery }, { to: "/faq", label: t.nav.faq }, { to: "/kontakt", label: t.nav.contact },
  ];
  const socials = [
    { key: "facebook", url: siteConfig.social.facebook, Icon: Facebook },
    { key: "instagram", url: siteConfig.social.instagram, Icon: Instagram },
  ].filter((s) => s.url);

  return (
    <footer className="grain bg-charcoal text-cream" data-testid="site-footer">
      <div className="container-x relative z-10 py-16 md:py-20">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <p className="font-serif text-3xl">Kola <span className="italic text-cream/70">Pension</span></p>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-cream/70">{t.footer.tagline}</p>
            <p className="mt-6 text-xs uppercase tracking-[0.2em] text-cream/50">{t.footer.management}</p>
            {socials.length > 0 && (
              <div className="mt-6 flex gap-3">
                {socials.map(({ key, url, Icon }) => (
                  <a key={key} href={url} target="_blank" rel="noopener noreferrer" className="rounded-full border border-cream/20 p-2.5 text-cream/80 transition-colors hover:border-clay hover:text-clay" aria-label={key} data-testid={`social-${key}`}><Icon className="h-4 w-4" /></a>
                ))}
              </div>
            )}
          </div>
          <div className="md:col-span-3">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cream/50">{t.footer.pages}</p>
            <ul className="mt-5 grid gap-2.5">
              {pages.map((p) => (
                <li key={p.to}><Link to={p.to} className="text-sm text-cream/80 transition-colors hover:text-clay" data-testid={`footer-link-${p.to === "/" ? "home" : p.to.slice(1)}`}>{p.label}</Link></li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cream/50">{t.footer.contact}</p>
            <ul className="mt-5 grid gap-3 text-sm">
              <li className="flex items-start gap-3 text-cream/80"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-clay" aria-hidden /><span data-testid="footer-address">{fullAddress()}</span></li>
              {siteConfig.contacts.map((c) => (
                <li key={c.email} className="flex flex-col gap-1.5">
                  <span className="text-cream/50 text-xs">{c.name}</span>
                  <a href={`tel:${c.phoneHref}`} className="flex items-center gap-3 text-cream/80 transition-colors hover:text-clay" data-testid={`footer-phone-${c.phoneHref}`} onClick={() => track("phone_click")}><Phone className="h-4 w-4 text-clay" aria-hidden />{c.phone}</a>
                  <a href={`mailto:${c.email}`} className="flex items-center gap-3 text-cream/80 transition-colors hover:text-clay break-all" data-testid={`footer-email-${c.name.split(" ")[0].toLowerCase()}`} onClick={() => track("email_click")}><Mail className="h-4 w-4 text-clay" aria-hidden />{c.email}</a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-14 flex flex-col gap-4 border-t border-cream/10 pt-8 text-xs text-cream/50 md:flex-row md:items-center md:justify-between">
          <p>© {year} {siteConfig.name}. {t.footer.rights}</p>
          <div className="flex flex-wrap gap-5">
            <Link to="/impressum" className="transition-colors hover:text-clay" data-testid="footer-link-impressum">{t.nav.impressum}</Link>
            <Link to="/datenschutz" className="transition-colors hover:text-clay" data-testid="footer-link-datenschutz">{t.nav.datenschutz}</Link>
            <button type="button" className="transition-colors hover:text-clay" onClick={() => window.dispatchEvent(new CustomEvent("kp:open-cookie-settings"))} data-testid="footer-cookie-settings">{t.nav.cookies}</button>
          </div>
        </div>
        <p className="mt-6 text-center text-xs text-cream/40" data-testid="footer-designed-by">{t.footer.designedBy}</p>
      </div>
    </footer>
  );
};
